import React from 'react';
import { FaUser, FaIdCard, FaPhone, FaEnvelope, FaHome, FaCheckCircle, FaTimesCircle, FaEdit, FaDownload } from 'react-icons/fa';

const QuoteCard = ({ quote, calculateNights, getStatusBadge, onUpdateStatus, onEdit, onDownload }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 md:gap-3">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 w-full sm:w-auto">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <FaUser className="text-green-600" size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 truncate">{quote.full_name}</h3>
              <p className="text-xs text-gray-500 truncate">ID: {quote.id.slice(0, 8)}...</p>
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto">
            {getStatusBadge(quote.status)}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 md:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
          {/* Información Personal */}
          <div className="space-y-2 md:space-y-3">
            <h4 className="font-semibold text-gray-900 text-xs md:text-sm uppercase tracking-wide mb-2 md:mb-3 flex items-center gap-2">
              <div className="w-1 h-3 md:h-4 bg-blue-500 rounded"></div>
              Información Personal
            </h4>
            <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
              <p className="flex items-center gap-2">
                <FaIdCard className="text-gray-400 flex-shrink-0" size={14} />
                <span className="font-medium text-gray-900 truncate">{quote.dni}</span>
              </p>
              <p className="flex items-center gap-2">
                <FaPhone className="text-gray-400 flex-shrink-0" size={14} />
                <a href={`tel:${quote.phone}`} className="font-medium text-gray-900 hover:text-green-600 truncate">{quote.phone}</a>
              </p>
              <p className="flex items-start gap-2">
                <FaEnvelope className="text-gray-400 mt-0.5 flex-shrink-0" size={14} />
                <a href={`mailto:${quote.email}`} className="font-medium text-gray-900 hover:text-green-600 break-all">{quote.email}</a>
              </p>
              <p className="flex items-center gap-2">
                <span className="text-gray-400 flex-shrink-0">📍</span>
                <span className="font-medium text-gray-900 truncate">{quote.city_of_origin}</span>
              </p>
            </div>
          </div>

          {/* Fechas y Huéspedes */}
          <div className="space-y-2 md:space-y-3">
            <h4 className="font-semibold text-gray-900 text-xs md:text-sm uppercase tracking-wide mb-2 md:mb-3 flex items-center gap-2">
              <div className="w-1 h-3 md:h-4 bg-purple-500 rounded"></div>
              Estadía
            </h4>
            <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
              <div className="bg-purple-50 p-2 md:p-3 rounded-lg">
                <p className="text-xs text-gray-500 mb-0.5 md:mb-1">Check-in</p>
                <p className="font-bold text-purple-700 text-xs md:text-sm">{new Date(quote.checkin_date).toLocaleDateString('es-PE', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</p>
              </div>
              <div className="bg-purple-50 p-2 md:p-3 rounded-lg">
                <p className="text-xs text-gray-500 mb-0.5 md:mb-1">Check-out</p>
                <p className="font-bold text-purple-700 text-xs md:text-sm">{new Date(quote.checkout_date).toLocaleDateString('es-PE', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</p>
              </div>
              <div className="bg-blue-50 p-2 md:p-3 rounded-lg border border-blue-200">
                <p className="text-center text-base md:text-lg font-semibold text-blue-800">
                  {calculateNights(quote.checkin_date, quote.checkout_date)} {calculateNights(quote.checkin_date, quote.checkout_date) === 1 ? 'noche' : 'noches'}
                </p>
              </div>
              <div className="flex gap-2 mt-2 md:mt-3">
                <div className="flex-1 bg-blue-50 p-2 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Adultos</p>
                  <p className="font-bold text-blue-700 text-base md:text-lg">{quote.number_of_guests}</p>
                </div>
                <div className="flex-1 bg-blue-50 p-2 rounded-lg text-center">
                  <p className="text-xs text-gray-500">Niños</p>
                  <p className="font-bold text-blue-700 text-base md:text-lg">{quote.number_of_children}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Información Adicional */}
          <div className="space-y-2 md:space-y-3">
            <h4 className="font-semibold text-gray-900 text-xs md:text-sm uppercase tracking-wide mb-2 md:mb-3 flex items-center gap-2">
              <div className="w-1 h-3 md:h-4 bg-green-500 rounded"></div>
              Casa Completa
            </h4>
            <div className="bg-green-50 p-3 md:p-4 rounded-lg space-y-1.5 md:space-y-2">
              <div className="flex items-center gap-2 mb-1 md:mb-2">
                <FaHome className="text-green-600 flex-shrink-0" size={16} />
                <span className="font-semibold text-gray-900 text-sm md:text-base">Alquiler Completo</span>
              </div>
              <p className="text-xs md:text-sm text-gray-700">
                • Todas las habitaciones disponibles
              </p>
              <p className="text-xs md:text-sm text-gray-700">
                • Uso exclusivo de las instalaciones
              </p>
            </div>
            
            {quote.price && (
              <div className="bg-blue-50 p-3 md:p-4 rounded-lg border border-blue-200">
                <p className="text-xs text-gray-500 mb-0.5 md:mb-1">Precio Cotizado</p>
                <p className="text-xl md:text-2xl font-bold text-blue-800">S/. {parseFloat(quote.price).toFixed(2)}</p>
              </div>
            )}
            
            {quote.status === 'pending' && (
              <div className="bg-yellow-50 p-2 md:p-3 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-800 text-center">
                  ⚠️ Cotización pendiente de confirmación
                </p>
              </div>
            )}
            
            {quote.notes && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Notas:</p>
                <p className="text-xs md:text-sm text-gray-700 italic break-words">{quote.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-3 md:pt-4 border-t border-gray-200">
          {quote.status === 'pending' && (
            <>
              <button 
                onClick={() => onUpdateStatus(quote.id, 'confirmed')} 
                className="flex items-center gap-1.5 md:gap-2 bg-green-500 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-green-600 transition-colors"
              >
                <FaCheckCircle size={12} />
                Confirmar
              </button>
              <button 
                onClick={() => onUpdateStatus(quote.id, 'cancelled')} 
                className="flex items-center gap-1.5 md:gap-2 bg-red-500 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-red-600 transition-colors"
              >
                <FaTimesCircle size={12} />
                Anular
              </button>
            </>
          )}
          <button 
            onClick={() => onDownload(quote)}
            className="flex items-center gap-1.5 md:gap-2 bg-blue-500 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-blue-600 transition-colors"
          >
            <FaDownload size={12} />
            <span className="hidden sm:inline">Descargar</span>
            <span className="sm:hidden">PDF</span>
          </button>
          <button 
            onClick={() => onEdit(quote)}
            className="flex items-center gap-1.5 md:gap-2 bg-gray-600 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            <FaEdit size={12} />
            Editar
          </button>
        </div>

        {/* Footer */}
        <div className="mt-3 md:mt-4 pt-2 md:pt-3 border-t border-gray-100 text-xs text-gray-400">
          Creada el {new Date(quote.created_at).toLocaleString('es-PE')}
        </div>
      </div>
    </div>
  );
};

export default QuoteCard;
